import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles, Heart, Award, Truck } from "lucide-react";
import { ASSETS, BRAND, buildWhatsAppLink } from "../lib/brand";
import { fetchProducts, fetchTestimonials } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { SectionHeader } from "../components/SectionHeader";
import { useReveal } from "../hooks/useReveal";

const categoryTiles = [
  {
    id: "nuts",
    title: "Premium Nuts",
    subtitle: "Pistachios · Almonds · Cashews",
    image: ASSETS.nutsCategory,
  },
  {
    id: "chocolates",
    title: "Luxury Chocolates",
    subtitle: "Belgian · Wafer · Konnafetto",
    image: ASSETS.chocolateCategory,
  },
  {
    id: "dry_fruits",
    title: "Exotic Dry Fruits",
    subtitle: "Kiwi · Papaya · Pineapple",
    image: ASSETS.dryFruitsCategory,
  },
];

const whyUs = [
  { icon: Sparkles, title: "Fresh Quality Products", text: "Sourced and roasted with care — every batch tasted before it reaches you." },
  { icon: Award,    title: "Premium Selection",       text: "Hand-picked origins from Turkey, Belgium, USA and India." },
  { icon: Heart,    title: "Ideal For Gifting",       text: "Elegant packaging crafted for weddings, holidays, and occasions." },
  { icon: Truck,    title: "Trusted Service",         text: "WhatsApp order in seconds. Open 8:30 AM – Midnight, all week." },
];

export default function Home() {
  const ref = useReveal();
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true }).then(setFeatured).catch(() => {});
    fetchTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <div ref={ref} data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
      >
        <img
          src={ASSETS.storefront}
          alt="Qamar Al Zahran Roastery storefront"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-brand-ivory pt-24 pb-20">
          <img
            src={ASSETS.logo}
            alt="Qamar Al Zahran logo"
            className="h-32 w-32 sm:h-36 sm:w-36 object-contain mx-auto bg-brand-ivory rounded-2xl p-1 mb-6 shadow-2xl animate-fade-in"
          />
          <span className="inline-block text-[11px] tracking-[0.36em] uppercase text-brand-gold animate-fade-in-up">
            Premium Roastery · UAE
          </span>
          <h1
            data-testid="hero-headline"
            className="mt-4 font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] animate-fade-in-up"
            style={{ animationDelay: "120ms" }}
          >
            Premium Flavors
            <br />
            <span className="text-brand-gold">For Every Occasion</span>
          </h1>
          <p
            className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-brand-ivory/85 leading-relaxed animate-fade-in-up"
            style={{ animationDelay: "240ms" }}
          >
            Quality nuts, delicious sweets, premium dry fruits, and chocolates —
            crafted for everyday enjoyment and memorable gifting.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up"
            style={{ animationDelay: "360ms" }}
          >
            <Link
              to="/products"
              data-testid="hero-explore-products-btn"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-brown px-7 py-3.5 rounded-full font-semibold hover:bg-[#E5C158] transition-all hover:shadow-2xl hover:-translate-y-0.5"
            >
              Explore Products <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-brand-ivory/60 text-brand-ivory px-7 py-3.5 rounded-full font-medium hover:bg-brand-ivory hover:text-brand-brown transition-all"
            >
              Contact Us
            </Link>
            <a
              href={buildWhatsAppLink("Hello Qamar Al Zahran! I'd like to place an order.")}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#1DA851] transition-all hover:shadow-2xl"
            >
              <MessageCircle className="h-4 w-4" />
              Order on WhatsApp
            </a>
          </div>

          <div className="mt-12 text-xs tracking-[0.24em] uppercase text-brand-ivory/60 animate-fade-in" style={{ animationDelay: "500ms" }}>
            {BRAND.hours}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section data-testid="about-preview-section" className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="reveal relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_24px_60px_rgba(59,30,26,0.18)]">
              <img src={ASSETS.interiorShelves} alt="Inside the roastery" className="zoom-img w-full h-full object-cover aspect-[4/5]" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-brand-gold text-brand-brown px-6 py-5 rounded-2xl shadow-xl">
              <div className="font-serif-display text-3xl leading-none">10+</div>
              <div className="text-[11px] tracking-[0.2em] uppercase mt-1">Years of crafting flavor</div>
            </div>
          </div>
          <div className="reveal">
            <span className="text-[11px] tracking-[0.28em] uppercase text-brand-gold font-semibold">Our Story</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-brown">
              A roastery rooted in tradition,<br />refined for today.
            </h2>
            <p className="mt-5 text-brand-muted leading-relaxed">
              Qamar Al Zahran began with a simple belief — that every nut, every chocolate, and every
              piece of fruit should be sourced with intention and shared with joy. From our shelves in
              the UAE to your celebrations, we curate flavors worth gathering around.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-link"
              className="inline-flex items-center gap-2 mt-8 text-brand-brown font-medium gold-underline"
            >
              Learn more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid="categories-section" className="py-20 md:py-28 bg-brand-beige/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Curated Categories"
            title="Three traditions. One roastery."
            subtitle="From hand-picked nuts to silky chocolates and sun-dried fruits — every collection tells its own story."
            testId="categories-header"
          />
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {categoryTiles.map((c, i) => (
              <Link
                key={c.id}
                to={`/products?category=${c.id}`}
                data-testid={`category-tile-${c.id}`}
                className="reveal group relative aspect-[4/5] rounded-3xl overflow-hidden block"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={c.image} alt={c.title} loading="lazy" className="zoom-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3B1E1A] via-[#3B1E1A]/40 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-7 text-brand-ivory">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-brand-gold">{c.subtitle}</span>
                  <h3 className="font-serif-display text-3xl mt-1">{c.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm">
                    Shop collection <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section data-testid="featured-products-section" className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Featured This Season"
            title="Our most-loved selections"
            subtitle="A taste of what makes Qamar Al Zahran special — picked, packed, and ready to delight."
            testId="featured-products-header"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featured.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
          <div className="text-center mt-12 reveal">
            <Link
              to="/products"
              data-testid="view-all-products-btn"
              className="inline-flex items-center gap-2 bg-brand-brown text-brand-ivory px-8 py-3.5 rounded-full font-medium hover:bg-[#5A2F2A] transition-all hover:shadow-xl"
            >
              View All Products <ArrowRight className="h-4 w-4 text-brand-gold" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-us-section" className="py-20 md:py-28 bg-brand-brown text-brand-ivory relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="reveal max-w-2xl mx-auto text-center mb-14">
            <span className="text-[11px] tracking-[0.28em] uppercase text-brand-gold font-semibold">Why Qamar Al Zahran</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl">
              Crafted with intention, <span className="text-brand-gold">shared with pride.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((w, i) => (
              <div
                key={w.title}
                className="reveal p-7 rounded-2xl bg-[#4a2620]/60 border border-[#6b4741]/40 hover:border-brand-gold transition-colors"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="h-11 w-11 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center mb-4">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif-display text-xl mb-2">{w.title}</h3>
                <p className="text-sm text-brand-ivory/75 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM PREVIEW */}
      <section data-testid="instagram-preview-section" className="py-20 md:py-28 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Follow the Roastery"
            title="@Qamar_al_zahran"
            subtitle="Step inside our world — daily flavors, gifting ideas, and behind-the-scenes moments."
            testId="instagram-header"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              ASSETS.storefront,
              ASSETS.interiorStaff,
              ASSETS.interiorShelves,
              ASSETS.giftBox,
              ASSETS.nutsCategory,
              ASSETS.chocolateCategory,
              ASSETS.dryFruitsCategory,
              ASSETS.giftBox2,
            ].map((src, i) => (
              <a
                key={i}
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`ig-tile-${i}`}
                className="reveal group relative aspect-square overflow-hidden rounded-xl block"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <img src={src} loading="lazy" alt="instagram preview" className="zoom-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-brand-brown/0 group-hover:bg-brand-brown/40 transition-colors flex items-center justify-center">
                  <span className="text-brand-ivory text-sm opacity-0 group-hover:opacity-100 transition-opacity">View on Instagram</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="py-20 md:py-28 bg-brand-beige/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Words from our customers"
              title="Loved across the UAE"
              testId="testimonials-header"
            />
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t, i) => (
                <figure
                  key={t.id}
                  className="reveal bg-brand-ivory p-8 rounded-2xl shadow-[0_6px_24px_rgba(59,30,26,0.08)]"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="text-brand-gold text-sm mb-3">★★★★★</div>
                  <blockquote className="font-serif-display text-lg text-brand-brown leading-snug">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-5 text-sm text-brand-muted">
                    <span className="font-semibold text-brand-brown">{t.name}</span>
                    {t.role && <> · {t.role}</>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
